import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Tag, message, Pagination, ConfigProvider, Spin, Select, Row, Col, Modal, Descriptions, Card, Tooltip, Dropdown, Space, Menu } from "antd";
import { ReloadOutlined, DownloadOutlined, FileTextOutlined, EyeOutlined, MoreOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { MakeApiCall } from "../../../../apis";
import dayjs from "dayjs";
import { WrapperInner } from "./innerStyle";
import axios from "axios";
import { ENDPOINT } from "../../../../config";

// Custom hook to get window size
const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

const ExportManager = ({ userData, filterData }) => {
    const token = userData?.token;

    // Pagination State
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({});
    const [exportList, setExportList] = useState([]);

    const [detailsVisible, setDetailsVisible] = useState(false);
    const [detailsData, setDetailsData] = useState(null);

    // Detail modal state for mobile view
    const [detailModal, setDetailModal] = useState({
        visible: false,
        record: null
    });

    // Filter state
    const [filters, setFilters] = useState({
        type: null,
        status: null
    });

    // Get window size and determine if mobile
    const windowSize = useWindowSize();
    const isMobile = windowSize.width < 768;

    // Fetch export logs with filters
    const fetchExportLogs = async (p = page, ps = pageSize, currentFilters = filters) => {
        if (!token) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('page', p);
            queryParams.append('per_page', ps);
            
            if (currentFilters.type) {
                queryParams.append('type', currentFilters.type);
            }
            if (currentFilters.status) {
                queryParams.append('status', currentFilters.status);
            }

            const response = await MakeApiCall(
                `data-logs/exports?${queryParams.toString()}`,
                "get",
                null,
                true,
                {},
                token
            );
            if (response?.status) {
                setExportList(Array.isArray(response?.data?.records) ? response.data.records : []);
                setTotal(Number(response?.data?.pagination?.total) || 0);
            } else {
                setExportList([]);
                setTotal(0);
                message.warning(response?.message || "Failed to fetch export logs");
            }
        } catch (e) {
            setExportList([]);
            setTotal(0);
            message.error("Error while loading export logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchExportLogs(page, pageSize, filters);
    }, [token, page, pageSize, filters]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPage(1);
    };

    const handleRefresh = () => {
        if (!token) return;
        message.loading("Refreshing export logs...", 1);
        fetchExportLogs(1, pageSize, filters).finally(() => {
            message.success("Export logs refreshed successfully");
        });
    };

    const handleDownload = async (exportId, fileName = "downloaded-file") => {
        setActionLoading((prev) => ({ ...prev, [exportId]: "download" }));
        try {
            const response = await axios({
                method: "get",
                url: `${ENDPOINT}data-logs/exports/${exportId}/download`,
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${userData?.token}`,
                },
            });

            const contentType = response?.headers?.["content-type"] || "application/octet-stream";
            const contentDisposition = response?.headers?.["content-disposition"] || "";

            // Some APIs may send error JSON as blob; detect and show message.
            if (contentType.includes("application/json")) {
                const errorText = await response.data.text();
                try {
                    const parsed = JSON.parse(errorText);
                    message.error(parsed?.message || "Failed to download file");
                } catch (e) {
                    message.error("Failed to download file");
                }
                return;
            }

            const blob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], { type: contentType });

            let finalFileName = fileName;

            // Support filename*=UTF-8'' and filename=
            const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
            if (utfMatch?.[1]) {
                finalFileName = decodeURIComponent(utfMatch[1]);
            } else if (basicMatch?.[1]) {
                finalFileName = basicMatch[1];
            }

            if (!/\.[a-z0-9]+$/i.test(finalFileName)) {
                if (contentType.includes("spreadsheetml")) finalFileName += ".xlsx";
                else if (contentType.includes("csv")) finalFileName += ".csv";
                else if (contentType.includes("zip")) finalFileName += ".zip";
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = finalFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || "Download failed";
            message.error(errorMsg);
            console.error("Download failed:", error);
        } finally {
            setActionLoading((prev) => ({ ...prev, [exportId]: null }));
        }
    };

    const handleDownload1 = async (exportId, fileName) => {
        setActionLoading(prev => ({ ...prev, [exportId]: 'download' }));
        try {
            const response = await MakeApiCall(
                `data-logs/exports/${exportId}/download`,
                "get",
                null,
                true,
                {},
                token,
                { responseType: 'blob' }
            );

            if (response?.data) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                
                let downloadFileName = fileName || `export_${exportId}.xlsx`;
                const contentDisposition = response.headers?.['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        downloadFileName = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
                
                link.setAttribute('download', downloadFileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                message.success('File downloaded successfully');
            } else {
                message.error('Failed to download file');
            }
        } catch (error) {
            console.error('Download error:', error);
            message.error(error?.response?.data?.message || 'Failed to download file');
        } finally {
            setActionLoading(prev => ({ ...prev, [exportId]: null }));
        }
    };

    const handleViewDetails = async (exportId) => {
        setActionLoading(prev => ({ ...prev, [exportId]: 'details' }));
        try {
            const response = await MakeApiCall(
                `data-logs/exports/${exportId}`,
                "get",
                null,
                true,
                {},
                token
            );
    
            if (response?.status && response?.data) {
                setDetailsData(response.data);
                setDetailsVisible(true);
            } else {
                message.error('Failed to fetch export details');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to fetch export details');
        } finally {
            setActionLoading(prev => ({ ...prev, [exportId]: null }));
        }
    };

    const getStatusColor = (status) => {
        if (typeof status !== "string") return "default";
        const s = status.toLowerCase();
        if (s === "completed" || s === "completed_with_warnings" || s === "done" || s === "ready") return "success";
        if (s === "processing" || s === "in_progress" || s === "generating") return "processing";
        if (s === "failed" || s === "error") return "error";
        return "default";
    };

    const getStatusIcon = (status) => {
        if (typeof status !== "string") return null;
        const s = status.toLowerCase();
        if (s === "completed" || s === "completed_with_warnings" || s === "done" || s === "ready") {
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        }
        if (s === "processing" || s === "in_progress" || s === "generating") {
            return <LoadingOutlined style={{ color: '#1890ff' }} />;
        }
        if (s === "failed" || s === "error") {
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        }
        return null;
    };

    // Render detail modal for mobile view
    const renderDetailModal = () => {
        if (!detailModal.record) return null;
        
        const record = detailModal.record;
        const status = typeof record.status === "string" ? record.status.toLowerCase() : "";
        const canDownload = status === "completed" || status === "completed_with_warnings" || status === "done" || status === "ready";
        const isLoading = actionLoading[record.id];
        
        return (
            <Modal
                title="Export Details"
                open={detailModal.visible}
                onCancel={() => setDetailModal({ visible: false, record: null })}
                footer={[
                    <Button key="close" onClick={() => setDetailModal({ visible: false, record: null })}>
                        Close
                    </Button>
                ]}
                width={isMobile ? '95%' : 600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <div className="export-detail-modal">
                    <div className="detail-section">
                        <div className="detail-row">
                            <span className="detail-label">File Name:</span>
                            <span className="detail-value fw-bold">{record.original_name || 'No name'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">File Size:</span>
                            <span className="detail-value">{record.file_size || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Export Type:</span>
                            <span className="detail-value">
                                <Tag color="blue">{record.type_label || record.type || '-'}</Tag>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                                <Space>
                                    {getStatusIcon(record.status)}
                                    <Tag color={getStatusColor(record.status)}>
                                        {typeof record.status === "string"
                                            ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
                                            : record.status}
                                    </Tag>
                                </Space>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Created At:</span>
                            <span className="detail-value">
                                {record.created_at
                                    ? dayjs.unix(record.created_at).format("DD MMM YYYY - hh:mm A")
                                    : '-'}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Rows:</span>
                            <span className="detail-value">{record.total_rows == null ? "—" : record.total_rows.toLocaleString()}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">File ID:</span>
                            <span className="detail-value">
                                <code>{record.file_id || '—'}</code>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Actions:</span>
                            <span className="detail-value">
                                <Space>
                                    {canDownload && (
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownload(record.id, record.original_name)}
                                            loading={isLoading === 'download'}
                                        >
                                            Download
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        icon={<FileTextOutlined />}
                                        onClick={() => {
                                            setDetailModal({ visible: false, record: null });
                                            handleViewDetails(record.id);
                                        }}
                                        loading={isLoading === 'details'}
                                    >
                                        Full Details
                                    </Button>
                                </Space>
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    // Render card view for mobile
    const renderCardView = () => {
        if (loading) {
            return <div className="text-center py-5"><Spin /></div>;
        }

        if (exportList.length === 0) {
            return <div className="text-center py-5">No export logs found</div>;
        }

        return (
            <div className="export-cards-container">
                {exportList.map((record) => {
                    const status = typeof record.status === "string" ? record.status.toLowerCase() : "";
                    const canDownload = status === "completed" || status === "completed_with_warnings" || status === "done" || status === "ready";
                    const isLoading = actionLoading[record.id];
                    
                    return (
                        <Card 
                            key={record.id || record.file_id}
                            className="mb-3"
                            style={{ 
                                borderRadius: '8px',
                                borderLeft: `2px solid ${getStatusColor(record.status) === 'success' ? '#52c41a' : 
                                           getStatusColor(record.status) === 'processing' ? '#1890ff' :
                                           getStatusColor(record.status) === 'error' ? '#ff4d4f' : '#d9d9d9'}`,
                                backgroundColor: 'var(--app-surface)'
                            }}
                        >
                            <div className="card-content">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="fw-bolder fs-6 text-one">
                                        {record.original_name || 'No name'}
                                    </div>
                                    <div>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => setDetailModal({ visible: true, record })}
                                            icon={<EyeOutlined />}
                                            className="me-1"
                                        />
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    {canDownload && (
                                                        <Menu.Item 
                                                            key="download"
                                                            icon={<DownloadOutlined />}
                                                            onClick={() => handleDownload(record.id, record.original_name)}
                                                            disabled={isLoading === 'download'}
                                                        >
                                                            Download File
                                                        </Menu.Item>
                                                    )}
                                                    <Menu.Item 
                                                        key="details"
                                                        icon={<FileTextOutlined />}
                                                        onClick={() => handleViewDetails(record.id)}
                                                        disabled={isLoading === 'details'}
                                                    >
                                                        View Full Details
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                            trigger={['click']}
                                        >
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<MoreOutlined />}
                                            />
                                        </Dropdown>
                                    </div>
                                </div>
                                
                                {/* Main Fields - 5 Key Fields */}
                                <Row gutter={[8, 8]} className="mb-2">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Type:</span>
                                            <span className="ms-2 fw-semibold">{record.type_label || record.type || '-'}</span>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Status:</span>
                                            <span className="ms-2">
                                                <Space size={4}>
                                                    {getStatusIcon(record.status)}
                                                    <Tag color={getStatusColor(record.status)} size="small">
                                                        {typeof record.status === "string"
                                                            ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
                                                            : record.status}
                                                    </Tag>
                                                </Space>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <Row gutter={[8, 8]} className="mb-2">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Created:</span>
                                            <span className="ms-2">
                                                {record.created_at
                                                    ? dayjs.unix(record.created_at).format("DD MMM YYYY")
                                                    : '-'}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Rows:</span>
                                            <span className="ms-2 fw-semibold">{record.total_rows == null ? "—" : record.total_rows.toLocaleString()}</span>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div className="mb-2">
                                    <div className="fs-7">
                                        <span className="text-muted">File ID:</span>
                                        <Tooltip title={record.file_id}>
                                            <span className="ms-2 text-primary">
                                                {record.file_id?.substring(0, 12)}...
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                
                                {/* View Details Button */}
                                <Button 
                                    type="link" 
                                    className="p-0 mt-2"
                                    onClick={() => setDetailModal({ visible: true, record })}
                                >
                                    View all details <i className="ki-outline ki-arrow-right fs-6 ms-1"></i>
                                </Button>
                            </div>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const columns = useMemo(() => [
        {
            title: "File Name",
            dataIndex: "original_name",
            key: "original_name",
            width: 250,
            render: (text, record) => (
                <div>
                    <div className="fw-bolder">{record.original_name || <span className="text-muted">No name</span>}</div>
                    <div className="text-muted small">{record.file_size ? `${record.file_size}` : ""}</div>
                </div>
            )
        },
        {
            title: "Export Type",
            dataIndex: "type_label",
            key: "type_label",
            width: 170,
            render: (text, record) => record.type_label || record.type || <span className="text-muted">—</span>
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 135,
            render: (status, record) => (
                <Space>
                    {getStatusIcon(status)}
                    <Tag color={getStatusColor(status)}>
                        {typeof status === "string"
                            ? status.charAt(0).toUpperCase() + status.slice(1)
                            : status}
                    </Tag>
                </Space>
            )
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (created_at) =>
                created_at
                    ? dayjs.unix(created_at).format("DD MMM YYYY - hh:mm A")
                    : <span className="text-muted">—</span>
        },
        {
            title: "Rows",
            dataIndex: "total_rows",
            key: "total_rows",
            width: 100,
            align: "right",
            render: (total_rows) => (total_rows == null ? "—" : total_rows.toLocaleString())
        },
        {
            title: "File ID",
            dataIndex: "file_id",
            key: "file_id",
            width: 160,
            render: (file_id) =>
                file_id ? (
                    <Tooltip title={file_id}>
                        <span className="text-primary">{file_id.substring(0, 12)}...</span>
                    </Tooltip>
                ) : "—"
        },
        {
            title: "Action",
            key: "action",
            width: 220,
            render: (_, record) => {
                const status = typeof record.status === "string" ? record.status.toLowerCase() : "";
                const hasError = status === "failed" || status === "error";
                const canDownload = status === "completed" || status === "completed_with_warnings" || status === "done" || status === "ready";
                const isLoading = actionLoading[record.id];
                
                return (
                    <div className="d-flex gap-2">
                        {canDownload && (
                            <Button
                                color="primary" variant="filled"
                                className="fs-7 fw-bold"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(record.id, record.original_name)}
                                loading={isLoading === 'download'}
                            >
                                Download
                            </Button>
                        )}
                        {(hasError || !canDownload) && (
                            <Button
                                color={hasError ? "danger" : "primary"} 
                                variant="filled"
                                className="fs-7 fw-bold"
                                icon={<FileTextOutlined />}
                                onClick={() => handleViewDetails(record.id)}
                                loading={isLoading === 'details'}
                            >
                                Details
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ], [actionLoading]);

    // CSS styles for mobile view
    const mobileStyles = `
        .export-detail-modal .detail-section {
            margin-top: 0px;
        }
        
        .export-detail-modal .detail-row {
            display: flex;
            padding: 12px 0;
            border-bottom: 1px solid var(--app-border);
        }
        
        .export-detail-modal .detail-row:last-child {
            border-bottom: none;
        }
        
        .export-detail-modal .detail-label {
            width: 110px;
            font-weight: 500;
            color: var(--app-text-muted);
            flex-shrink: 0;
        }
        
        .export-detail-modal .detail-value {
            flex: 1;
            color: var(--app-text);
            word-break: break-word;
        }
        
        .export-cards-container .text-one {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            white-space: normal;
            line-height: 23px;
            width: 210px !important;
        }
        
        @media (max-width: 768px) {
            .ant-card-body {
                padding: 12px;
            }
            .filters .ant-row {
                margin-bottom: 10px;
            }
        }
    `;

    return (
        <WrapperInner>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bold">Export Audit Log</span>
                                <span className="text-gray-500 mt-1 fw-semibold fs-7">
                                    Generated files with status, destination, owner, and downloads.
                                </span>
                            </h3>
                            <div className="card-toolbar filters">
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Select
                                            placeholder="All Types"
                                            className="min-w-150px"
                                            size="large"
                                            style={{ width: '100%' }}
                                            value={filters.type}
                                            onChange={(value) => handleFilterChange('type', value)}
                                            allowClear
                                            options={filterData?.types?.map(type => ({
                                                label: type.label,
                                                value: type.value
                                            })) || []}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Select
                                            placeholder="All Statuses"
                                            className="min-w-150px"
                                            size="large"
                                            style={{ width: '100%' }}
                                            value={filters.status}
                                            onChange={(value) => handleFilterChange('status', value)}
                                            allowClear
                                            options={filterData?.statuses?.map(status => ({
                                                label: status.label,
                                                value: status.value
                                            })) || []}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            <div className="table-responsive">
                                <Spin spinning={loading}>
                                    {isMobile ? (
                                        renderCardView()
                                    ) : (
                                        <Table
                                            scroll={{ x: "max-content" }}
                                            columns={columns}
                                            dataSource={exportList}
                                            rowKey={row => row.id || row.file_id}
                                            pagination={false}
                                            components={{
                                                table: (p) => (
                                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />
                                                ),
                                                header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> },
                                            }}
                                        />
                                    )}
                                </Spin>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-body py-2">
                            <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                                <Pagination
                                    className="mt-3"
                                    current={page}
                                    pageSize={pageSize}
                                    total={total}
                                    showSizeChanger
                                    responsive={true}
                                    simple={isMobile}
                                    onChange={(p, s) => {
                                        setPage(p);
                                        setPageSize(s);
                                    }}
                                    showTotal={(t, r) =>
                                        isMobile ? `Total: ${t}` : `${r[0]}-${r[1]} of ${t} items`
                                    }
                                />
                            </ConfigProvider>
                        </div>
                    </div>
                </div>
            </div>

            {/* Full Details Modal */}
            <Modal
                title="Export Full Details"
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={null}
                width={isMobile ? '95%' : 800}
                centered
            >
                {detailsData && (
                    <Descriptions column={isMobile ? 1 : 2} bordered size="small">
                        <Descriptions.Item label="File ID" span={isMobile ? 1 : 2}>
                            {detailsData.file_id || "—"}
                        </Descriptions.Item>

                        <Descriptions.Item label="File Name" span={isMobile ? 1 : 2}>
                            {detailsData.original_name || "—"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Type">
                            {detailsData.type_label || detailsData.type}
                        </Descriptions.Item>

                        <Descriptions.Item label="Status">
                            <Space>
                                {getStatusIcon(detailsData.status)}
                                <Tag color={getStatusColor(detailsData.status)}>
                                    {detailsData.status}
                                </Tag>
                            </Space>
                        </Descriptions.Item>

                        <Descriptions.Item label="Message" span={isMobile ? 1 : 2}>
                            <span className="text-danger">{detailsData.message || "—"}</span>
                        </Descriptions.Item>

                        <Descriptions.Item label="Filters" span={isMobile ? 1 : 2}>
                            <pre style={{ margin: 0, maxHeight: '200px', overflow: 'auto' }}>
                                {JSON.stringify(detailsData.filter_params, null, 2)}
                            </pre>
                        </Descriptions.Item>

                        <Descriptions.Item label="Created At">
                            {detailsData.created_at
                                ? dayjs.unix(detailsData.created_at).format("DD MMM YYYY - hh:mm A")
                                : "—"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Started At">
                            {detailsData.started_at
                                ? dayjs.unix(detailsData.started_at).format("DD MMM YYYY - hh:mm A")
                                : "—"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Finished At" span={isMobile ? 1 : 2}>
                            {detailsData.finished_at
                                ? dayjs.unix(detailsData.finished_at).format("DD MMM YYYY - hh:mm A")
                                : "—"}
                        </Descriptions.Item>

                        {detailsData.total_rows != null && (
                            <Descriptions.Item label="Total Rows">
                                {detailsData.total_rows.toLocaleString()}
                            </Descriptions.Item>
                        )}

                        {detailsData.file_size && (
                            <Descriptions.Item label="File Size">
                                {detailsData.file_size}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Detail Modal for Mobile Card View */}
            {renderDetailModal()}
            
            {/* Mobile Styles */}
            <style>{mobileStyles}</style>
        </WrapperInner>
    );
};

export default ExportManager;